import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { PopUpWindow } from '@xing-com/pop-up-window';

import IframePrescreen from '../consumers/prescreen-consumer/iframe-prescreen';
import type {
  BrazeCustomEvent,
  BrazeUserAttribute,
  PrescreenPayload,
} from '../consumers/prescreen-consumer/prescreen-consumer';
import {
  ALT_IMAGE_MISSING_EVENT_NAME,
  SIGNUP_MODAL_BUTTON_IDS,
} from '../events';
import { shouldReportMissingAlt } from '../utils/alt-text';
import { parseMetaLanguageSchema } from '../utils/custom-meta-language';
import type { HtmlSerializerProps } from '../utils/html-serializer';
import { Logger } from '../utils/logger';

import { LightBoxFooter } from './light-box-footer';
import { Styled } from './light-box.styles';
import { MetaLanguageRenderer } from './meta-language-renderer';

interface LightBoxProps {
  data: PrescreenPayload;
  onClose: () => void;
  onButtonClick?: (buttonId: string) => void;
  onPrescreenClick: () => void;
  onPrescreenButtonClick: () => void;
  onContentShown: () => void;
  setBrazeCustomAttribute: (payload: BrazeUserAttribute) => void;
  onURLButtonClick: (
    url: string,
    target?: '_self' | '_blank',
    buttonId?: string
  ) => void;
  sendBrazeCustomEvent: (payload: BrazeCustomEvent) => void;
}

const ValidatedPopupImage = ({
  imageUrl,
  altImage,
  fallbackAlt,
  onClick,
  sendBrazeCustomEvent,
}: {
  imageUrl: string;
  altImage?: string;
  fallbackAlt?: string;
  onClick: () => void;
  sendBrazeCustomEvent: (payload: BrazeCustomEvent) => void;
}): ReactNode | null => {
  const eventSent = useRef(false);
  const missingAlt = shouldReportMissingAlt(altImage);

  useEffect(() => {
    if (missingAlt && !eventSent.current) {
      sendBrazeCustomEvent({ name: ALT_IMAGE_MISSING_EVENT_NAME });
      eventSent.current = true;
    }
  }, [missingAlt, sendBrazeCustomEvent]);

  if (missingAlt) return null;

  return (
    <Styled.PopupWindowImage
      src={imageUrl}
      alt={altImage || fallbackAlt}
      title={altImage || fallbackAlt}
      onClick={onClick}
    />
  );
};

const isValidUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'https:';
    // oxlint-disable-next-line typescript/no-unused-vars
  } catch (err) {
    return false;
  }
};

interface IframePrescreenSchema {
  type: 'iframe';
  url: string;
  title?: string;
  iframeTitle?: string;
  introScreen?: HtmlSerializerProps['content'][];
  introScreenButtonText?: string;
  introScreenButtonAriaLabel?: string;
}

const isIframePrescreenSchema = (
  value: unknown
): value is IframePrescreenSchema => {
  if (typeof value !== 'object' || value === null) return false;

  return (
    'type' in value &&
    value.type === 'iframe' &&
    'url' in value &&
    typeof value.url === 'string' &&
    (!('title' in value) || typeof value.title === 'string') &&
    (!('iframeTitle' in value) || typeof value.iframeTitle === 'string') &&
    (!('introScreen' in value) || Array.isArray(value.introScreen)) &&
    (!('introScreenButtonText' in value) ||
      typeof value.introScreenButtonText === 'string') &&
    (!('introScreenButtonAriaLabel' in value) ||
      typeof value.introScreenButtonAriaLabel === 'string')
  );
};

const LightBox = ({
  data,
  onClose,
  onButtonClick,
  onPrescreenClick,
  onPrescreenButtonClick,
  onContentShown,
  sendBrazeCustomEvent,
  onURLButtonClick,
}: LightBoxProps): ReactNode | null => {
  const [popUpWindowVisible, setPopUpWindowVisible] = useState(true);
  const onContentShownRef = useRef(onContentShown);
  const onCloseRef = useRef(onClose);

  onContentShownRef.current = onContentShown;
  onCloseRef.current = onClose;

  useEffect(() => {
    if (popUpWindowVisible) {
      onContentShownRef.current();
    } else {
      onCloseRef.current();
    }
  }, [data.campaignId, popUpWindowVisible]);

  const popUpWindowHide = (): void => setPopUpWindowVisible(false);
  const closeButtonProps = {
    onClick: () => {
      onButtonClick?.(SIGNUP_MODAL_BUTTON_IDS.CLOSE);
      popUpWindowHide();
    },
  };
  let popUpWindowContent;
  let noPadding = true;
  let ariaLabel: string | undefined = data.title;

  const popUpWindowActions =
    !data.isHTML && data.hasButton ? (
      <LightBoxFooter
        button1AriaLabel={data.button1AriaLabel}
        button2AriaLabel={data.button2AriaLabel}
        buttonColor={data.buttonColor}
        button1Color={data.button1Color}
        button2Color={data.button2Color}
        buttons={data.buttons}
        onClick={onPrescreenButtonClick}
        onClose={onClose}
      ></LightBoxFooter>
    ) : null;

  if (data.isHTML && data.message) {
    noPadding = false;
    const sanitizedMessage = data.message.replace(/(\r\n|\n|\r)/gm, '');
    const jsonString = sanitizedMessage.match(
      /<PrescreenJson>(.*)<\/PrescreenJson>/
    )?.[1];

    if (jsonString === undefined) return null;

    try {
      const schema: unknown = JSON.parse(jsonString);
      const metaLanguage = parseMetaLanguageSchema(schema);

      if (metaLanguage) {
        ariaLabel = metaLanguage.title || undefined;
        popUpWindowContent = (
          <MetaLanguageRenderer
            key={data.campaignId}
            metaLanguage={metaLanguage}
            onClose={metaLanguage.type === 'custom' ? popUpWindowHide : onClose}
            onButtonClick={onButtonClick}
            onURLButtonClick={onURLButtonClick}
            sendBrazeCustomEvent={sendBrazeCustomEvent}
          />
        );
      } else if (isIframePrescreenSchema(schema)) {
        // don't allow invalid url
        if (!isValidUrl(schema.url)) {
          Logger.log('Braze provider - invalid url');
          return null;
        }
        noPadding = true;
        ariaLabel = schema.title || undefined;
        popUpWindowContent = (
          <IframePrescreen
            url={schema.url}
            title={schema.iframeTitle}
            introScreen={schema.introScreen}
            onClose={onClose}
            introScreenButtonText={schema.introScreenButtonText}
            introScreenButtonAriaLabel={schema.introScreenButtonAriaLabel}
          />
        );
      }
    } catch (e) {
      Logger.log(e);
      return null;
    }
  } else if (data.template === 'full_background') {
    popUpWindowContent = (
      <Styled.FullSizeDiv
        backgroundImageURL={data.template ? data.imageUrl : undefined}
        mobileBackgroundImageURL={data.mobileImageURL}
        onClick={onPrescreenClick}
      >
        <Styled.FullBackgroundFooter>
          {popUpWindowActions}
        </Styled.FullBackgroundFooter>
      </Styled.FullSizeDiv>
    );

    return (
      <PopUpWindow
        data-qa="lightbox-pop-up-window"
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        isShown={popUpWindowVisible}
        hide={data.removeCrossIcon ? undefined : popUpWindowHide}
        closeButtonProps={closeButtonProps}
        noPadding={noPadding}
        onDimmerClick={popUpWindowHide}
      >
        {popUpWindowContent}
      </PopUpWindow>
    );
  } else if (data.messageClickAction === 'URI' && data.messageUrl) {
    popUpWindowContent = (
      <ValidatedPopupImage
        imageUrl={data.imageUrl}
        altImage={data.altImage}
        fallbackAlt={data.buttonText || data.messageUrl}
        onClick={onPrescreenClick}
        sendBrazeCustomEvent={sendBrazeCustomEvent}
      />
    );
  } else {
    popUpWindowContent = (
      <ValidatedPopupImage
        imageUrl={data.imageUrl}
        altImage={data.altImage}
        fallbackAlt={data.buttonText || data.messageUrl}
        onClick={onPrescreenClick}
        sendBrazeCustomEvent={sendBrazeCustomEvent}
      />
    );
  }

  return (
    <PopUpWindow
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      data-qa="lightbox-pop-up-window"
      isShown={popUpWindowVisible}
      hide={data.removeCrossIcon ? undefined : popUpWindowHide}
      closeButtonProps={closeButtonProps}
      noPadding={noPadding}
      onDimmerClick={popUpWindowHide}
    >
      {popUpWindowContent}
      <div>{popUpWindowActions}</div>
    </PopUpWindow>
  );
};

export { LightBox };
